/* eslint react-hooks/exhaustive-deps: "warn" */

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { AccessRoutes } from 'ui/organisms/AccessRoutes/AccessRoutes';
import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';

import styles from './RegisApiPage.module.scss';

import { getFaqItems } from './RegisApiPage.data';
import { RegisComparison } from './sections/RegisComparison/RegisComparison';
import { RegisConsole } from './sections/RegisConsole/RegisConsole';
import { RegisGovernance } from './sections/RegisGovernance/RegisGovernance';
import { RegisHero } from './sections/RegisHero/RegisHero';
import { RegisIntegration } from './sections/RegisIntegration/RegisIntegration';
import { RegisModes } from './sections/RegisModes/RegisModes';
import { RegisProvenance } from './sections/RegisProvenance/RegisProvenance';
import { RegisSchemes } from './sections/RegisSchemes/RegisSchemes';

type Translate = (key: string) => string;

const getConstraintRoutes = (t: Translate): AccessRoute[] => [
  {
    name: t('Retrieval first'),
    desc: (
      <>
        <em>{t('No answer without a record.')}</em>{' '}
        {t(
          'The question is resolved to entities and fields, those fields are read from primary registry records, and the answer is written from the retrieved values. Nothing in the response originates in model weights.',
        )}
      </>
    ),
  },
  {
    name: t('Refusal is a result'),
    desc: (
      <>
        <em>{t('Empty is a valid response.')}</em>{' '}
        {t('Where the register does not publish a field, Regis returns the gap rather than an estimate.')}{' '}
        <strong>
          {t('An empty answer with a stated reason is safer in a case file than a confident one you cannot source.')}
        </strong>
      </>
    ),
  },
  {
    name: t('Same question, same answer'),
    desc: (
      <>
        <em>{t('Reproducible for review.')}</em>{' '}
        {t(
          'Answers are reconstructed from the underlying record, so a reviewer running the query again gets the same values, with the retrieval timestamp attached to show what the register held at the time.',
        )}
      </>
    ),
  },
  {
    name: t('Structured output'),
    desc: (
      <>
        <em>{t('JSON, not paragraphs.')}</em>{' '}
        {t(
          'Alongside the written answer the response carries the resolved entities, the fields used and the source records, so it can be written straight into a case management system without parsing prose.',
        )}
      </>
    ),
  },
];

export const RegisApiPage = () => {
  const { t } = useInt();

  return (
    <div className={styles.wrapper}>
      <RegisHero
        title={t('Registry data, queried in plain language.')}
        description={t(
          'Regis takes a question in natural language, retrieves from primary government registry records across 200+ countries, and returns the answer with the filing behind every value.',
        )}
        actions={
          <>
            <Button type="blue" size="xxl" rounded="full" href="https://platform.globaldatabase.com/app-api/api">
              {t('Generate a test key')}
            </Button>
            <Button type="dark-outline" size="xxl" rounded="full" href="https://api.globaldatabase.com/docs/v2/#setup">
              {t('Read the docs')}
            </Button>
          </>
        }
      />

      <RegisConsole />

      <AccessRoutes
        background="navy"
        numbered
        heading={t('The model can only return what it retrieved.')}
        lede={t(
          'A general-purpose model answers a company question from whatever it absorbed in training. There is no filing behind that answer, no date attached to it, and no way for a reviewer to check it. Regis is built the other way round: the question triggers retrieval against registry records, and the answer is assembled from what came back.',
        )}
        routes={getConstraintRoutes(t)}
      />

      <RegisComparison />

      <RegisModes />

      <RegisIntegration />

      <RegisProvenance />

      <RegisSchemes />

      <RegisGovernance />

      <CtaSection
        theme="dark"
        spotlight
        title={t('Ask it something you already know the answer to.')}
        description={t(
          'That is the fastest way to judge it. Pick a company in your book, ask Regis a question you can verify, and check the filing it returns.',
        )}
        actions={
          <>
            <Button type="white" size="xl" rounded="full" href="/free-trial">
              {t('Start a free trial')}
            </Button>
            <Button type="white-outline" size="xl" rounded="full" href="/book-a-demo">
              {t('Book a technical walkthrough')}
            </Button>
          </>
        }
      />

      <Faq className={styles.faq} title={t('Common questions.')} data={getFaqItems(t)} />
    </div>
  );
};
